import React, { useState, useEffect } from "react";
import type { ChartWidgetConfig } from "./ChartWidget";
import type { DataRow } from "./ChartDataTable";
import { ChartDataSourceModal } from "./ChartDataSourceModal";
import { Database } from "lucide-react";

interface ChartWidgetConfigProps {
  initialConfig: Partial<ChartWidgetConfig>;
  onChange: (config: ChartWidgetConfig) => void;
}

// Parse data array into DataRows for table input (multi-series)
function parseDataToRows(
  data?: Array<{ label: string; [key: string]: string | number }>,
  series?: string[]
): DataRow[] {
  if (!data || data.length === 0) {
    const initialRow: DataRow = { label: "" };
    series?.forEach((s) => (initialRow[s] = ""));
    return [initialRow]; // Start with 1 empty row
  }
  return data.map((d) => {
    const row: DataRow = { label: d.label };
    series?.forEach((s) => {
      row[s] = d[s]?.toString() || "";
    });
    return row;
  });
}

// Parse DataRows back into data array (multi-series)
function parseRowsToData(
  rows: DataRow[],
  series: string[]
): Array<{ label: string; [key: string]: string | number }> {
  return rows
    .filter((row) => row.label) // Only include rows with labels
    .map((row) => {
      const dataPoint: { label: string; [key: string]: string | number } = {
        label: row.label,
      };
      series.forEach((s) => {
        dataPoint[s] = parseFloat(row[s]) || 0;
      });
      return dataPoint;
    });
}

// Generate CSV input string from rows (multi-series)
function generateCsvInput(rows: DataRow[], series: string[]): string {
  const header = `Label,${series.join(",")}`;
  const dataRows = rows
    .filter((row) => row.label)
    .map((row) => `${row.label},${series.map((s) => row[s] || "0").join(",")}`)
    .join("\n");
  return header + "\n" + dataRows;
}

export function ChartWidgetConfigForm({
  initialConfig = {},
  onChange,
}: ChartWidgetConfigProps) {
  const [title, setTitle] = useState(initialConfig?.title || "");
  const [chartType, setChartType] = useState<
    "line" | "bar" | "column" | "pie" | "area"
  >(initialConfig?.chartType || "line");
  const [series, setSeries] = useState<string[]>(
    initialConfig?.series && initialConfig.series.length > 0
      ? initialConfig.series
      : ["Value"]
  );
  const [seriesColors, setSeriesColors] = useState<Record<string, string>>(
    initialConfig?.seriesColors || { Value: "#3b82f6" }
  );
  const [dataRows, setDataRows] = useState<DataRow[]>(
    parseDataToRows(initialConfig?.data, initialConfig?.series)
  );
  const [pieSelectedSeries, setPieSelectedSeries] = useState<string>(
    initialConfig?.pieSelectedSeries || initialConfig?.series?.[0] || "Value"
  );
  const [showLegend, setShowLegend] = useState<boolean>(
    initialConfig?.showLegend !== undefined ? initialConfig.showLegend : true
  );
  const [backgroundColor, setBackgroundColor] = useState(
    initialConfig?.backgroundColor || ""
  );
  const [xAxisLabel, setXAxisLabel] = useState(initialConfig?.xAxisLabel || "");
  const [yAxisLabel, setYAxisLabel] = useState(initialConfig?.yAxisLabel || "");
  const [isDataModalOpen, setIsDataModalOpen] = useState(false);

  // Live update on every change
  useEffect(() => {
    const data = parseRowsToData(dataRows, series);
    const csvInput = generateCsvInput(dataRows, series);

    const config: ChartWidgetConfig = {
      title: title || "Untitled Chart",
      chartType,
      data,
      series,
      seriesColors,
      pieSelectedSeries: chartType === "pie" ? pieSelectedSeries : undefined,
      showLegend,
      backgroundColor: backgroundColor || undefined,
      csvInput,
      xAxisLabel: xAxisLabel || undefined,
      yAxisLabel: yAxisLabel || undefined,
    };
    onChange(config);
  }, [
    title,
    chartType,
    dataRows,
    series,
    seriesColors,
    pieSelectedSeries,
    showLegend,
    backgroundColor,
    xAxisLabel,
    yAxisLabel,
  ]); // DON'T include onChange

  // Validation
  const hasValidData = dataRows.some((row) => {
    if (!row.label) return false;
    return series.some((s) => row[s] && !isNaN(parseFloat(row[s])));
  });
  const isValid = title.trim() !== "" && hasValidData;

  return (
    <div className="space-y-4 flex-1 overflow-auto">
      {/* Widget Background - Prominent */}
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Widget Background
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={backgroundColor || "#ffffff"}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-400 dark:border-gray-500"
            title="Widget background color"
          />
          <div className="flex-1">
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-1"
              maxLength={7}
            />
            {backgroundColor && (
              <button
                onClick={() => setBackgroundColor("")}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                title="Clear background"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Widget Title *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Monthly Revenue"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          maxLength={100}
        />
        {!title && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            Title is required
          </p>
        )}
      </div>

      {/* Chart Type */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Chart Type
        </label>
        <select
          value={chartType}
          onChange={(e) =>
            setChartType(
              e.target.value as "line" | "bar" | "column" | "pie" | "area"
            )
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="line">📈 Line Chart</option>
          <option value="column">📊 Column Chart</option>
          <option value="bar">📊 Bar Chart (Horizontal)</option>
          <option value="pie">🥧 Pie Chart</option>
          <option value="area">📉 Area Chart</option>
        </select>
      </div>

      {/* Pie Chart Series Selector - Only show for pie charts */}
      {chartType === "pie" && series.length > 1 && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Pie Chart Series
          </label>
          <select
            value={pieSelectedSeries}
            onChange={(e) => setPieSelectedSeries(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            {series.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select which data series to display in the pie chart
          </p>
        </div>
      )}

      {/* Legend Toggle */}
      <div>
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            Show Legend
          </span>
          <div className="relative">
            <input
              type="checkbox"
              checked={showLegend}
              onChange={(e) => setShowLegend(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-600 dark:peer-focus:ring-blue-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Toggle legend visibility below chart
        </p>
      </div>

      {/* Data Source Button */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Data Source *
        </label>
        <button
          onClick={() => setIsDataModalOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
        >
          <div className="flex items-center gap-2">
            <Database size={20} className="text-blue-600" />
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                Manual Entry
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {dataRows.length} rows × {series.length} columns
              </div>
            </div>
          </div>
          <div className="text-blue-600 font-semibold text-sm">Edit Data →</div>
        </button>

        {/* Validation Error for Data */}
        {!hasValidData && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            At least one data row required with valid label and number value
          </p>
        )}
      </div>

      {/* X-Axis Label */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          X-Axis Label (optional)
        </label>
        <input
          type="text"
          value={xAxisLabel}
          onChange={(e) => setXAxisLabel(e.target.value)}
          placeholder="e.g., Month"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          maxLength={50}
        />
      </div>

      {/* Y-Axis Label */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Y-Axis Label (optional)
        </label>
        <input
          type="text"
          value={yAxisLabel}
          onChange={(e) => setYAxisLabel(e.target.value)}
          placeholder="e.g., Revenue"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          maxLength={50}
        />
      </div>

      {/* Validation Status (hidden, just for info) */}
      {!isValid && (
        <div className="hidden">
          Validation failed: {!title && "title required"}{" "}
          {!hasValidData && "data required"}
        </div>
      )}

      {/* Data Source Modal */}
      <ChartDataSourceModal
        isOpen={isDataModalOpen}
        onClose={() => setIsDataModalOpen(false)}
        rows={dataRows}
        series={series}
        seriesColors={seriesColors}
        onRowsChange={setDataRows}
        onSeriesChange={setSeries}
        onSeriesColorsChange={setSeriesColors}
        onSave={() => {
          // Data is already saved via state, just close
          setIsDataModalOpen(false);
        }}
      />
    </div>
  );
}
